/* eslint-disable no-magic-numbers */
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from 'carbon-react/lib/components/typography';
import Button from 'carbon-react/lib/components/button';
import Link from 'carbon-react/lib/components/link';
import Box from 'carbon-react/lib/components/box';

import { ExcelEntityConfig } from '@futrli/views/ExcelFilesView';
import { useExcelFilesView } from '@futrli/views/hooks/useExcelFilesView';

import styles from './excel-modal-content.scss';
import { downloadExcelFile } from '../hooks/utils';

interface Props {
  workspaceId: string;
  organisationId: string;
  config: ExcelEntityConfig;
  guidelinesUrl: string;
}

export const UpdateFromExcelModalContent = ({ workspaceId, organisationId, config, guidelinesUrl }: Props) => {
  const { files, isFetchingFiles } = useExcelFilesView(workspaceId, organisationId, config);
  const latestFile = files[0];

  const handleDownload = useCallback(() => {
    if (latestFile) {
      downloadExcelFile(latestFile.file_url, latestFile.file_name);
    }
  }, [latestFile]);

  return (
    <Box className={styles.container}>
      <Typography fontSize="16px" fontWeight="500">
        <FormattedMessage id="predict.update_excel_data" defaultMessage="Update Excel data" />
      </Typography>
      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 1 }} />
      </Typography>
      <Typography fontSize="14px" mb="8px" color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))">
        <FormattedMessage
          id="predict.excel_org_update.step_1"
          defaultMessage="Download your current data. To use past data, choose from your update history below."
        />
      </Typography>
      <Button
        buttonType="secondary"
        mb="16px"
        iconType="download"
        iconPosition="after"
        onClick={handleDownload}
        disabled={isFetchingFiles || !latestFile}
      >
        <FormattedMessage id="predict.excel.download_current_data" defaultMessage="Download current data" />
      </Button>
      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 2 }} />
      </Typography>
      <Typography fontSize="14px" color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))" mb="16px">
        <FormattedMessage
          id="predict.update_data_following_guidelines"
          defaultMessage="Update data following the <link>import guidelines (opens in new tab)</link>."
          values={{
            link: chunks => (
              <Link href={guidelinesUrl} target="_blank" rel="noreferrer">
                <span style={{ fontWeight: 500 }}>{chunks}</span>
              </Link>
            )
          }}
        />
        {config.entityType === 'driver' && (
          <>
            {' '}
            <FormattedMessage
              id="predict.drivers_stored_daily_aggregate_daily_figures"
              defaultMessage="Note: drivers can be stored daily: we will aggregate daily figures into the monthly amounts displayed on the Driver’s page."
            />
          </>
        )}
        {config.entityType === 'forecast' && (
          <>
            {' '}
            <FormattedMessage
              id="predict.note_new_update_replace_previous_excel_completely"
              defaultMessage="Note: New uploads <bold>will replace the previous Excel upload completely</bold>."
              values={{
                bold: chunks => <span style={{ fontWeight: 500 }}>{chunks}</span>
              }}
            />
          </>
        )}
      </Typography>
      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 3 }} />
      </Typography>
      <Typography fontSize="14px" mb={0} color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))">
        <FormattedMessage
          id="predict.upload_data_file_max_size_25.step_3"
          defaultMessage="Upload the updated file. Max file size is 25mb."
        />
      </Typography>
    </Box>
  );
};
